'use client'

import { type Hit } from 'instantsearch.js'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type AlgoliaProxyRecord } from '../../../../algolia'
import { useExperimentsContext } from '../../../../experiments/components/ExperimentsContext'
import { experimentAccessorIsMarketplace } from '../../../../experiments/model/experimentAccessor'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  type ProductSearchResultItemNowDetails,
  useProductSearchResultItemNowDetails,
} from '../useProductSearchResultItemNowDetails'

type ProductSearchResultItemValue = {
  algoliaRecord: Hit<AlgoliaProxyRecord>
  isMarketplace: boolean
  nowDetails: ProductSearchResultItemNowDetails
  query?: string
}

const ProductSearchResultItemContext =
  createContext<ProductSearchResultItemValue | null>(null)

type ProductSearchResultItemContextProviderProps = PropsWithChildren<{
  algoliaRecord: Hit<AlgoliaProxyRecord>
  query?: string
}>

export const ProductSearchResultItemContextProvider: FC<
  ProductSearchResultItemContextProviderProps
> = ({ algoliaRecord, children, query }) => {
  const globalConfig = useGlobalConfigContext()
  const { experiments } = useExperimentsContext()

  const isMarketplace = experimentAccessorIsMarketplace(
    experiments,
    globalConfig.tenant,
  )

  const nowDetails = useProductSearchResultItemNowDetails({
    currentVariant: algoliaRecord,
    tenant: globalConfig.tenant,
  })

  const value = useMemo(
    () => ({
      algoliaRecord,
      isMarketplace,
      nowDetails,
      query,
    }),
    [algoliaRecord, isMarketplace, nowDetails, query],
  )

  return (
    <ProductSearchResultItemContext.Provider value={value}>
      {children}
    </ProductSearchResultItemContext.Provider>
  )
}

export const useProductSearchResultItemContext =
  (): ProductSearchResultItemValue => {
    const context = useContext(ProductSearchResultItemContext)

    if (context === null) {
      throw new Error(
        'useProductSearchResultItemContext must be used within a ProductSearchResultItemContextProvider',
      )
    }

    return context
  }
